import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ms from 'ms'
import { Drawer } from '@adc/ui-core'
import {
  gtmOnboardingDrawer,
  gtmOnboardingDrawerExit,
} from '../../shared/services/tracking'
import OnboardingDrawerContent from './OnboardingDrawerContent'
import { AUTH_TYPES } from '../authToggle/constants'
import { useApplicationModals, ModalIds } from '../auctionApplication/modals'
import { useApplicationContext } from '../auctionApplication/context'

const RENDER_DELAY = ms('10s')

const OnboardingDrawer = ({ close, elmId, hideIfEmbedded, startTour }) => {
  const modals = useApplicationModals()
  const { user, embeddedContext, featureFlags } = useApplicationContext()

  const [open, setOpen] = useState(false)
  const [delayingRender, setDelayingRender] = useState(true)

  const isWelcomeSliderDisabled =
    featureFlags?.['fe.resi.no-welcome-slider'] === true

  /**
   * Before taking further actions we need to wait for the Drawer to completely
   * open or close. Returned a promise with a timeOut for this reason.
   * This also allows the user to see the drawer animation when getting displayed.
   */
  const setDisplay = open =>
    setTimeout(() => {
      setOpen(open)
    }, 750)

  useEffect(() => {
    const loggedIn = user.logged_in

    const alreadyOnboarded = Boolean(
      window.localStorage.getItem('onboarding_started') || loggedIn
    )

    if (!alreadyOnboarded && !isWelcomeSliderDisabled) {
      setDisplay(true)
      gtmOnboardingDrawer()
    }

    setTimeout(() => {
      setDelayingRender(false)
      // Mark onboarding as started only once the render delay elapses and the
      // drawer actually displays. Setting it earlier (on mount) would burn the
      // flag if the user navigates away before the delay, preventing the drawer
      // from ever showing.
      if (!alreadyOnboarded && !isWelcomeSliderDisabled) {
        window.localStorage.setItem('onboarding_started', true)
      }
    }, RENDER_DELAY)
  }, [isWelcomeSliderDisabled, user.logged_in])

  const onClose = async (track = true) => {
    if (track) gtmOnboardingDrawerExit()
    setDisplay(false)
    close?.()
  }

  const startTutorial = async () => {
    await onClose(false)
    startTour?.()
  }

  const getStarted = async () => {
    await onClose(false)
    modals.open(ModalIds.Authentication, {
      authInitialActive: AUTH_TYPES.SIGNUP,
    })
  }

  if (
    isWelcomeSliderDisabled ||
    delayingRender ||
    (hideIfEmbedded && embeddedContext === 'webview')
  )
    return null
  if (modals.getActiveModalId()) {
    onClose()
    return null
  }

  return (
    <Drawer
      displayPartially
      open={open}
      onClose={onClose}
      elmId={elmId}
      headerStyle='clear'
      closeOnDimmerClick={false}
    >
      <OnboardingDrawerContent
        elmId={elmId}
        getStarted={getStarted}
        onClose={onClose}
        startTutorial={startTour ? startTutorial : undefined}
      />
    </Drawer>
  )
}

OnboardingDrawer.propTypes = {
  close: PropTypes.func.isRequired,
  elmId: PropTypes.string.isRequired,
  hideIfEmbedded: PropTypes.bool,
  startTour: PropTypes.func,
}

export default OnboardingDrawer
